import { useState, useRef, useCallback } from "react";

const SWIPE_THRESHOLD = 80;
const DISMISS_DISTANCE = 400;
const MOBILE_BREAKPOINT = 768;

export const useSwipeDismiss = (onDismiss) => {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const startRef = useRef(null);

  const dismiss = useCallback(
    (finalX, finalY) => {
      setIsDismissing(true);
      setOffset({ x: finalX, y: finalY });
      setTimeout(onDismiss, 200);
    },
    [onDismiss],
  );

  const onPointerDown = useCallback((e) => {
    if (e.target.closest("button")) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    startRef.current = { x: e.clientX, y: e.clientY };
    setIsDragging(true);
  }, []);

  const onPointerMove = useCallback((e) => {
    if (!startRef.current) return;
    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;

    // Constrain to the dismiss direction only
    setOffset(
      isMobile
        ? { x: 0, y: Math.min(dy, 0) }
        : { x: Math.min(dx, 0), y: 0 },
    );
  }, []);

  const onPointerUp = useCallback(
    (e) => {
      if (!startRef.current) return;
      const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
      const dx = e.clientX - startRef.current.x;
      const dy = e.clientY - startRef.current.y;
      startRef.current = null;
      setIsDragging(false);

      if (isMobile) {
        if (dy < -SWIPE_THRESHOLD) { dismiss(0, -DISMISS_DISTANCE); return; }
      } else {
        if (dx < -SWIPE_THRESHOLD) { dismiss(-DISMISS_DISTANCE, 0); return; }
      }

      setOffset({ x: 0, y: 0 });
    },
    [dismiss],
  );

  const style = {
    "--_swipe-x": `${offset.x}px`,
    "--_swipe-y": `${offset.y}px`,
    ...(isDragging ? { transition: "none" } : {}),
    ...(isDismissing ? { opacity: 0 } : {}),
    touchAction: "none",
    userSelect: "none",
  };

  return {
    style,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
    },
  };
};
